import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import LabeledTextInput from 'components/consumer/LabeledTextInput';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { useQuery } from '@apollo/client';
import { GET_CLOSEST_STORE_ZIPCODE } from 'mocks/queries';
import storesData from 'hooks/useClosestStoreZipcode/stores.json';
import { ZIP_CODE_REGEX, ZIPCODE_ERROR_MESSAGES } from 'commons/constants';
import useSetZipCodeFromUser from 'global-state/zipCodeFromUser/useSetZipCodeFromUser';
import ShowroomLocatorResults from './ShowroomLocatorResults';

const validateZipcodeInput = inputs => {
  if (!inputs.zipcode.toString().trim()) {
    return { zipcode: ZIPCODE_ERROR_MESSAGES.ZIPCODE_IS_EMPTY };
  }

  if (!ZIP_CODE_REGEX.test(inputs.zipcode)) {
    return { zipcode: ZIPCODE_ERROR_MESSAGES.ZIPCODE_IS_INVALID_FORMAT };
  }

  return {};
};

const ShowroomLocatorContent = ({
  inputId = 'showroom-zipcode',
  className = '',
}) => {
  const [searchZipcode, setSearchZipcode] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const zipInputRef = useRef();
  const setZipCodeFromUser = useSetZipCodeFromUser();

  const { data: storeZipData, loading, error } = useQuery(
    GET_CLOSEST_STORE_ZIPCODE,
    {
      variables: { zipcode: searchZipcode },
      skip: !hasSearched || !searchZipcode,
      errorPolicy: 'all',
    }
  );

  const storeZip = storeZipData?.getClosestStoreFromZipcode;

  let storeLocation = null;

  if (storeZip && storeZip !== 'virtual' && storeZip !== 'null') {
    // API found a valid store
    storeLocation = storesData[storeZip];
  } else if (hasSearched && !loading && searchZipcode) {
    storeLocation = storesData[searchZipcode];
  }

  const handleSearch = useCallback(
    zipcode => {
      const trimmedZipCode = zipcode.trim();
      setSearchZipcode(trimmedZipCode);
      setHasSearched(true);
      setZipCodeFromUser(trimmedZipCode);
    },
    [setZipCodeFromUser]
  );

  const handleViewAll = useCallback(() => {
    setSearchZipcode('');
    setHasSearched(false);
  }, []);

  return (
    <div className={className}>
      <Formik
        initialValues={{ zipcode: '' }}
        validate={validateZipcodeInput}
        onSubmit={({ zipcode }) => {
          handleSearch(zipcode);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <LabeledTextInput
              id={inputId}
              name="zipcode"
              label="Zip Code"
              type="text"
              value={values.zipcode}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={
                touched.zipcode && errors.zipcode ? errors.zipcode : null
              }
              maxLength="5"
              autoComplete="postal-code"
              ref={zipInputRef}
              className="text-sm mb-1"
              action={
                <CtaButton
                  type="submit"
                  styling="solid-button"
                  color="brand"
                  size="sm"
                  className="ml-2"
                >
                  Search
                </CtaButton>
              }
              actionHasPadding={false}
              actionStretchHeight
            />
          </form>
        )}
      </Formik>

      <div className="text-xs text-gray font-normal mb-2">Within 50 miles</div>

      {hasSearched && (
        <ShowroomLocatorResults
          loading={loading}
          error={error}
          storeLocation={storeLocation}
        />
      )}

      <div className="mt-3">
        <Link
          to="/showrooms/"
          onClick={handleViewAll}
          className="text-brand underline text-sm"
        >
          View All
        </Link>
      </div>
    </div>
  );
};

ShowroomLocatorContent.propTypes = {
  inputId: PropTypes.string,
  className: PropTypes.string,
};

export default ShowroomLocatorContent;
